import { useCallback, useEffect, useRef, useState } from "react";
import "./UpdateBanner.css";

/**
 * Shows a small top banner when a newer build of the PWA is available.
 *
 * Detection relies on the service worker update lifecycle: each release stamps a
 * unique version into service-worker.js (see scripts/stamp-version.js), so the
 * browser notices the change, installs the new worker, and parks it in the
 * "waiting" state. We surface that as a banner; tapping "Update" tells the
 * waiting worker to activate and then reloads every open window of the app.
 */
export default function UpdateBanner() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [visible, setVisible] = useState(false);
  const updateClickedRef = useRef(false);

  const showUpdate = useCallback((worker) => {
    if (!worker) return;
    setWaitingWorker(worker);
    setVisible(true);
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return undefined;
    }

    let registrationRef = null;

    // Only reload once, and only when the user actually asked to update — this
    // avoids an unwanted reload from clients.claim() on the very first install.
    const onControllerChange = () => {
      if (!updateClickedRef.current) return;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    const trackWorker = (worker) => {
      if (!worker) return;
      worker.addEventListener("statechange", () => {
        if (worker.state === "installed" && navigator.serviceWorker.controller) {
          showUpdate(worker);
        }
      });
    };

    navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        if (!registration) return;
        registrationRef = registration;

        // A new worker may already be waiting from a background check.
        if (registration.waiting && navigator.serviceWorker.controller) {
          showUpdate(registration.waiting);
        }

        registration.addEventListener("updatefound", () => {
          trackWorker(registration.installing);
        });

        registration.update().catch(() => {});
      })
      .catch(() => {});

    const checkForUpdate = () => {
      if (document.visibilityState === "visible" && registrationRef) {
        registrationRef.update().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", checkForUpdate);
    const interval = setInterval(checkForUpdate, 60 * 60 * 1000);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", checkForUpdate);
      clearInterval(interval);
    };
  }, [showUpdate]);

  const handleUpdate = () => {
    updateClickedRef.current = true;
    if (waitingWorker) {
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
      // controllerchange fires once the new worker takes over -> reload.
      // Fallback in case the event doesn't fire (rare).
      setTimeout(() => window.location.reload(), 1500);
    } else {
      window.location.reload();
    }
  };

  if (!visible) return null;

  return (
    <div className="update-banner" role="status" aria-live="polite">
      <span className="update-banner-text">A new version is available.</span>
      <button type="button" className="update-banner-btn" onClick={handleUpdate}>
        Update
      </button>
      <button
        type="button"
        className="update-banner-dismiss"
        onClick={() => setVisible(false)}
        aria-label="Dismiss update notice"
      >
        &times;
      </button>
    </div>
  );
}
